"use client";

import { useLayoutEffect, useState } from "react";
import {
  copyKeyboardDefinition,
  matchesKeyboardBinding,
  type KeyboardBindingDefinition,
} from "./keyboard-binding-definition";

export interface GlobalKeyboardBinding {
  readonly definition: KeyboardBindingDefinition;
  readonly run: ((event: KeyboardEvent) => void) | undefined;
  /** Pure guard; rejection tries the next binding/scope without consuming the key. */
  readonly when?: (event: KeyboardEvent) => boolean;
}

export interface GlobalKeyboardScope {
  readonly id: string;
  /** Higher priorities run first; ties retain initial registration order. */
  readonly priority?: number;
  readonly enabled?: boolean;
  /** Block lower-ranked registrations even when this scope has no matching action. */
  readonly exclusive?: boolean;
  /** Defaults to the current document; null keeps the scope detached. */
  readonly document?: Document | null;
}

export interface GlobalKeyboardRegistration {
  readonly instanceId: symbol;
  readonly document: Document;
  readonly scope: string;
  readonly priority: number;
  readonly enabled: boolean;
  readonly exclusive: boolean;
  readonly bindings: readonly (KeyboardBindingDefinition & {
    readonly enabled: boolean;
    readonly guarded?: true;
  })[];
}

interface ScopeEntry {
  scope: GlobalKeyboardScope;
  bindings: readonly GlobalKeyboardBinding[];
}
interface DocumentRegistry {
  entries: Map<symbol, ScopeEntry>;
  listener: (event: KeyboardEvent) => void;
}

const documents = new Map<Document, DocumentRegistry>();

/** Committed global scopes only. No callbacks or application content are exposed. */
export function getGlobalKeyboardBindingRegistrations(): readonly GlobalKeyboardRegistration[] {
  return Array.from(documents, ([document, registry]) =>
    Array.from(registry.entries, ([instanceId, { scope, bindings }]) => ({
      instanceId,
      document,
      scope: scope.id,
      priority: scope.priority ?? 0,
      enabled: scope.enabled !== false,
      exclusive: scope.exclusive === true,
      bindings: bindings.map(({ definition, run, when }) => ({
        ...copyKeyboardDefinition(definition),
        enabled: run !== undefined,
        ...(when === undefined ? {} : { guarded: true as const }),
      })),
    })),
  ).flat();
}

/**
 * One bubbling document listener, shared by all migrated global scopes.
 * Local handlers run first. Already-prevented keys are always left alone.
 * Typing, composition and repeat policy stay explicit in each binding's guard.
 */
export function useGlobalKeyboardBindings(
  scope: GlobalKeyboardScope,
  bindings: readonly GlobalKeyboardBinding[],
) {
  const [instanceId] = useState(() => Symbol("global-keyboard-scope"));
  const doc =
    scope.document === undefined
      ? typeof document === "undefined"
        ? null
        : document
      : scope.document;

  // Publish only committed renders, before a browser event can observe stale callbacks.
  useLayoutEffect(() => {
    if (doc === null) {
      return;
    }

    let registry = documents.get(doc);

    if (registry === undefined) {
      const entries = new Map<symbol, ScopeEntry>();
      const listener = (event: KeyboardEvent) => dispatch(entries, event);
      registry = { entries, listener };
      documents.set(doc, registry);
      doc.addEventListener("keydown", listener);
    }

    registry.entries.set(instanceId, { scope: { ...scope }, bindings });
  });

  // Lifetime is independent of callback updates, preserving tie order across rerenders.
  useLayoutEffect(
    () => () => {
      if (doc === null) {
        return;
      }

      const registry = documents.get(doc);
      registry?.entries.delete(instanceId);

      if (registry?.entries.size === 0) {
        doc.removeEventListener("keydown", registry.listener);
        documents.delete(doc);
      }
    },
    [doc, instanceId],
  );
}

function dispatch(entries: Map<symbol, ScopeEntry>, event: KeyboardEvent) {
  if (event.defaultPrevented) {
    return;
  }

  const ordered = Array.from(entries).sort(
    ([, a], [, b]) => (b.scope.priority ?? 0) - (a.scope.priority ?? 0),
  );

  for (const [id, entry] of ordered) {
    // An earlier guard may synchronously unmount another scope.
    if (entries.get(id) !== entry || entry.scope.enabled === false) {
      continue;
    }

    for (const { definition, run, when } of entry.bindings) {
      if (
        run === undefined ||
        !matchesKeyboardBinding(event, definition) ||
        (when !== undefined && !when(event))
      ) {
        continue;
      }

      if (definition.preventDefault !== false) {
        event.preventDefault();
      }

      if (definition.stopPropagation === true) {
        event.stopPropagation();
      }

      run(event);
      return;
    }

    if (entry.scope.exclusive === true) {
      return;
    }
  }
}
